import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Alert,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import Selector from '../../../../components/Selector';
import { asignarCargoPrestamo } from '../../../../utils/generalsFetch';
import { formatNumberWithCommas } from '../../../../utils/generalFunctions';

export default function AsignarCargoDialog({ open, onClose, onSuccess, prestamo }) {
  const [categorias, setCategorias] = useState([]);
  const [idCategoria, setIdCategoria] = useState('');
  const [monto, setMonto] = useState('0.00');
  const [descripcion, setDescripcion] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingCats, setLoadingCats] = useState(false);
  const [error, setError] = useState('');

  const productoFinanciero =
    prestamo?.producto ||
    prestamo?.id_productofinanciero ||
    prestamo?.producto_financiero_id;

  useEffect(() => {
    if (!open) return;
    setError('');
    setIdCategoria('');
    setMonto('0.00');
    setDescripcion('');
    setCategorias([]);

    setLoadingCats(true);
    const idcompany = localStorage.getItem('idcompany');

    // Solo categorias de tipo 'especifico' que aplican a prestamo y estan activas.
    // Si el prestamo tiene producto financiero, las que coincidan se muestran primero.
    fetch(
      `${process.env.REACT_APP_SERVER_URL}/api/categorias_cargos/company/${idcompany}?aplicaA=prestamo&estado=activo&tipo=especifico`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': idcompany,
        },
      }
    )
      .then((r) => r.json())
      .then((data) => {
        if (!Array.isArray(data)) { setCategorias([]); return; }
        // Ordenar: primero las que matchean el producto financiero del prestamo
        const matchea = (cat) => {
          if (!productoFinanciero || !cat.categoria_prestamo) return false;
          try {
            const arr = typeof cat.categoria_prestamo === 'string'
              ? JSON.parse(cat.categoria_prestamo)
              : cat.categoria_prestamo;
            return Array.isArray(arr) && arr.some((x) => x?.id === productoFinanciero);
          } catch {
            return false;
          }
        };
        const ordered = [...data].sort((a, b) => {
          const am = matchea(a) ? 0 : 1;
          const bm = matchea(b) ? 0 : 1;
          return am - bm;
        });
        setCategorias(ordered);
      })
      .catch(() => setError('No se pudieron cargar las categorías de cargo'))
      .finally(() => setLoadingCats(false));
  }, [open, productoFinanciero]);

  const handleCategoriaChange = (e) => {
    const id = e.target.value;
    setIdCategoria(id);
    const cat = categorias.find((c) => c.id === id);
    if (cat && cat.montoFijo) {
      setMonto(parseFloat(cat.montoFijo).toFixed(2));
    }
  };

  const handleSubmit = async () => {
    setError('');
    if (!idCategoria) {
      setError('Selecciona una categoría');
      return;
    }
    const montoNum = parseFloat(String(monto).replace(/,/g, ''));
    if (!montoNum || montoNum <= 0) {
      setError('El monto debe ser mayor a 0');
      return;
    }
    setLoading(true);
    try {
      await asignarCargoPrestamo({
        idPrestamo: prestamo.id,
        idcompany: localStorage.getItem('idcompany'),
        usuarioId: localStorage.getItem('id'),
        payload: {
          id_categoria_cargo: idCategoria,
          monto_total: montoNum,
          descripcion,
        },
      });
      onSuccess && onSuccess();
      onClose();
    } catch (e) {
      setError(e.message || 'Error al asignar el cargo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle>
        <Typography variant="h6" fontWeight={700}>Asignar cargo al préstamo</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Stack>
            <Typography fontWeight={600} sx={{ ml: 1, mb: 0.5 }}>Categoría</Typography>
            {loadingCats ? (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Cargando categorías…</Typography>
              </Stack>
            ) : (
              <Selector
                value={idCategoria}
                onChange={handleCategoriaChange}
                options={categorias.map((c) => ({ value: c.id, label: c.nombre }))}
                placeholder="Seleccionar categoría"
              />
            )}
            {!loadingCats && categorias.length === 0 && !error && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                No hay categorías de cargo de tipo <strong>específico</strong> creadas.
                Ve a Gestión Comercial → Categorías de Cargos y crea una con tipo "Específico".
              </Typography>
            )}
          </Stack>

          <Stack>
            <Typography fontWeight={600} sx={{ ml: 1, mb: 0.5 }}>Monto</Typography>
            <TextField
              fullWidth
              value={formatNumberWithCommas(monto)}
              onChange={(e) => setMonto(e.target.value.replace(/,/g, ''))}
              InputProps={{ startAdornment: <InputAdornment position="start">RD$</InputAdornment> }}
            />
          </Stack>

          <Stack>
            <Typography fontWeight={600} sx={{ ml: 1, mb: 0.5 }}>Descripción (opcional)</Typography>
            <TextField
              fullWidth
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Motivo o nota del cargo"
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button
          color="primarybutton"
          variant="contained"
          sx={{ fontWeight: 700 }}
          onClick={handleSubmit}
          disabled={loading || !idCategoria}
        >
          {loading ? 'Asignando…' : 'Asignar cargo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
